import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { makePickSavingField } from '@helpers/makePickSavingField';

import { apiClient } from '@services/apiClient';

import {
  type ITotalCostSavingsLifetime,
  type ITotalCostSavingsLifetimeKeys,
} from '../model/totalCostSavings';

const totalCostSavingsLifetimeKey = (locationIds: Array<number>) => ['totalCostSavingsLifetime', locationIds];

const totalCostSavingsLifetimeFn = ({ queryKey }: QueryFunctionContext<string[]>) => {
  return apiClient.get<{ data: ITotalCostSavingsLifetime }>(
    `${APP_API_URL_V2}fsd/action-item/executive-summary/cost-savings-lifetime`,
    {
      params: {
        location_ids: queryKey[1],
      },
    },
  );
};

const totalCostSavingsLifetimeSelect =
  (locationIds: Array<number>) =>
  ({ data }: AxiosResponse<{ data: ITotalCostSavingsLifetime }>) => {
    const pick = makePickSavingField(!locationIds);

    const years = data.data.years.map((year) => ({
      value: year,
      label: year,
    }));

    const {
      potentialSavingsCustomerCurrency,
      potentialSavingsFacilityCurrency,
      totalSavingsCustomerCurrency,
      totalSavingsFacilityCurrency,
      verifiedSavingsCustomerCurrency,
      verifiedSavingsFacilityCurrency,
    } = data.data;

    return {
      potentialSavings: pick(potentialSavingsCustomerCurrency, potentialSavingsFacilityCurrency),
      totalSavings: pick(totalSavingsCustomerCurrency, totalSavingsFacilityCurrency),
      verifiedSavings: pick(verifiedSavingsCustomerCurrency, verifiedSavingsFacilityCurrency),
      years,
    };
  };

export const useTotalCostSavingsLifetimeQuery = ({ locationIds }: ITotalCostSavingsLifetimeKeys) => {
  return useQuery({
    // @ts-ignore TODO: update react-query to v5
    queryKey: totalCostSavingsLifetimeKey(locationIds),
    queryFn: totalCostSavingsLifetimeFn,
    select: totalCostSavingsLifetimeSelect(locationIds),
  });
};
